import { useEffect, useState } from 'react';
import { supabase, NewsPost } from '../lib/supabase';
import MetaTags from '../components/MetaTags';
import { Calendar, ArrowLeft, User } from 'lucide-react';

interface NewsArticlePageProps {
  id: string;
  onBack: () => void;
}

export default function NewsArticlePage({ id, onBack }: NewsArticlePageProps) {
  const [post, setPost] = useState<NewsPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadPost();
  }, [id]);

  const loadPost = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error: fetchError } = await supabase
        .from('news_posts')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (fetchError) throw fetchError;
      if (!data) throw new Error('Article not found');
      setPost(data as NewsPost);
    } catch (err) {
      console.error('Error loading news post:', err);
      setError(err instanceof Error ? err.message : 'Failed to load article');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-red-500 font-horizon text-2xl animate-pulse">Loading Article...</div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center flex-col gap-4">
        <div className="text-red-500 text-xl">Error: {error || 'Article not found'}</div>
        <button
          onClick={onBack}
          className="bg-[#ffed00] text-black px-6 py-3 font-bold hover:bg-[#ffed00]/90 transition-colors"
        >
          Back to News
        </button>
      </div>
    );
  }

  const newsDescription = post.content.substring(0, 155).replace(/<[^>]*>/g, '');
  const newsUrl = `https://yokzona.com/news/${id}`;
  const newsImage = post.image_url || 'https://yokzona.com/yok_logo_256.png';

  return (
    <div className="min-h-screen bg-black">
      <MetaTags
        title={`${post.title} | yOKzONa News`}
        description={newsDescription}
        image={newsImage}
        url={newsUrl}
        type="article"
      />
      <div className="fixed top-0 left-0 right-0 z-40 bg-black/95 backdrop-blur-sm border-b border-red-500/30">
        <div className="container mx-auto px-4 py-4">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-red-500 hover:text-red-400 group transition-colors"
          >
            <ArrowLeft className="group-hover:-translate-x-1 transition-transform" size={20} />
            <span className="font-bold">Back to News</span>
          </button>
        </div>
      </div>

      <div className="pt-24 pb-16">
        <article className="container mx-auto px-4 max-w-4xl">
          <div className="relative h-96 mb-12 rounded-xl overflow-hidden">
            <img
              src={post.image_url || 'https://images.pexels.com/photos/442576/pexels-photo-442576.jpeg'}
              alt={post.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <h1 className="font-horizon text-4xl md:text-6xl text-white mb-4 leading-tight">
                {post.title}
              </h1>
              <div className="flex items-center gap-6 text-gray-300">
                <div className="flex items-center gap-2">
                  <Calendar size={18} />
                  <span className="text-sm">
                    {new Date(post.published_at).toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </span>
                </div>
                {post.author && (
                  <div className="flex items-center gap-3">
                    {post.author_image_url ? (
                      <img
                        src={post.author_image_url}
                        alt={post.author}
                        className="w-10 h-10 rounded-full object-cover border-2 border-yellow-400/50"
                      />
                    ) : (
                      <User size={18} />
                    )}
                    <div className="flex flex-col">
                      <span className="text-xs text-gray-400">Written by</span>
                      <span className="text-sm font-semibold text-white">{post.author}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-red-900/10 to-black border border-red-600/20 rounded-xl p-8 md:p-12">
            {post.excerpt && (
              <div className="mb-8 pb-8 border-b border-red-600/20">
                <p className="text-xl text-gray-300 italic leading-relaxed">
                  {post.excerpt}
                </p>
              </div>
            )}

            <div className="prose prose-invert prose-lg max-w-none">
              <div className="text-gray-200 leading-relaxed whitespace-pre-wrap">
                {post.content}
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <button
              onClick={onBack}
              className="bg-[#ffed00] text-black px-8 py-4 font-bold text-lg hover:bg-[#ffed00]/90 transition-colors inline-flex items-center gap-2"
            >
              <ArrowLeft size={20} />
              Back to All News
            </button>
          </div>
        </article>
      </div>
    </div>
  );
}
